import Quill from 'quill';

const BlockEmbed = Quill.import('blots/block/embed');

class InputBlot extends BlockEmbed {
  static update(domNode, value) {
    // Find the input wrapper
    const wrapper = domNode.querySelector('.input-wrapper');
    if (!wrapper) return;
    
    // Update the label if present
    const label = wrapper.querySelector('.input-label');
    if (label) {
      if (value.label) {
        label.textContent = value.label;
        label.style.display = 'block';
      } else {
        label.style.display = 'none';
      }
    }
    
    // Find the input element
    const input = wrapper.querySelector('input');
    if (!input) return;
    
    // Update input properties
    input.type = value.type || 'text';
    input.placeholder = value.placeholder || '';
    input.value = value.defaultValue || '';
    input.name = value.name || '';
    
    // Update data attributes for PDF generation
    input.setAttribute('data-pdf-field', 'true');
    input.setAttribute('data-pdf-field-type', value.type || 'text');
    input.setAttribute('data-pdf-field-name', value.name || input.getAttribute('data-pdf-field-name') || `field_${Date.now()}`);
    
    if (value.required) {
      input.setAttribute('required', 'true');
    } else {
      input.removeAttribute('required');
    }
    
    // Update CSS classes
    if (value.cssClass) {
      input.className = value.cssClass;
      input.setAttribute('data-css-class', value.cssClass);
    } else {
      input.className = '';
      input.removeAttribute('data-css-class');
    }
    
    // Update container alignment
    domNode.style.textAlign = value.alignment || 'left';
    
    // Update input styles
    const styles = {
      width: value.fullWidth ? '100%' : (value.width || 'auto'),
      padding: value.size === 'small' ? '4px 8px' : value.size === 'large' ? '12px 16px' : '8px 12px',
      fontSize: value.size === 'small' ? '14px' : value.size === 'large' ? '18px' : '16px',
      borderRadius: `${value.borderRadius || 4}px`,
      border: `1px solid ${value.borderColor || '#ccc'}`,
      backgroundColor: value.backgroundColor || 'white',
      color: value.textColor || '#333',
      fontFamily: 'inherit',
      transition: 'all 0.3s ease',
    };
    
    // Apply styles
    Object.assign(input.style, styles);
    
    // Update stored data
    wrapper.setAttribute('data-input-config', JSON.stringify(value));
    
    // Apply validation pattern if provided
    if (value.pattern) {
      input.setAttribute('pattern', value.pattern);
    } else {
      input.removeAttribute('pattern');
    }
    
    // Update min/max for number inputs
    if (value.type === 'number') {
      if (value.min !== undefined) input.setAttribute('min', value.min);
      if (value.max !== undefined) input.setAttribute('max', value.max);
      if (value.step !== undefined) input.setAttribute('step', value.step);
    }
  }
  
  static create(value) {
    const node = super.create();
    
    // Apply container styles
    node.style.textAlign = value.alignment || 'left';
    node.style.margin = '16px 0';
    node.style.clear = 'both';
    
    // Create wrapper for input and label
    const wrapper = document.createElement('div');
    wrapper.className = 'input-wrapper';
    wrapper.style.display = 'inline-block';
    wrapper.style.width = value.fullWidth ? '100%' : 'auto';
    
    // Create label if provided
    if (value.label) {
      const label = document.createElement('label');
      label.className = 'input-label';
      label.textContent = value.label;
      label.style.display = 'block';
      label.style.marginBottom = '4px';
      label.style.fontSize = '14px';
      label.style.fontWeight = '500';
      label.style.color = '#333';
      wrapper.appendChild(label);
    }
    
    // Create the input element
    const input = document.createElement('input');
    input.type = value.type || 'text';
    input.placeholder = value.placeholder || '';
    input.value = value.defaultValue || '';
    input.name = value.name || '';
    
    // Add data attributes for PDF generation
    input.setAttribute('data-pdf-field', 'true');
    input.setAttribute('data-pdf-field-type', value.type || 'text');
    input.setAttribute('data-pdf-field-name', value.name || `field_${Date.now()}`);
    
    if (value.required) {
      input.setAttribute('required', 'true');
    }
    
    // Apply CSS classes if provided
    if (value.cssClass) {
      input.className = value.cssClass;
      input.setAttribute('data-css-class', value.cssClass);
    }
    
    // Apply validation pattern if provided
    if (value.pattern) {
      input.setAttribute('pattern', value.pattern);
    }
    
    // Apply min/max for number inputs
    if (value.type === 'number') {
      if (value.min !== undefined) input.setAttribute('min', value.min);
      if (value.max !== undefined) input.setAttribute('max', value.max);
      if (value.step !== undefined) input.setAttribute('step', value.step);
    }
    
    // Apply all the styles
    const styles = {
      width: value.fullWidth ? '100%' : (value.width || 'auto'),
      padding: value.size === 'small' ? '4px 8px' : value.size === 'large' ? '12px 16px' : '8px 12px',
      fontSize: value.size === 'small' ? '14px' : value.size === 'large' ? '18px' : '16px',
      borderRadius: `${value.borderRadius || 4}px`,
      border: `1px solid ${value.borderColor || '#ccc'}`,
      backgroundColor: value.backgroundColor || 'white',
      color: value.textColor || '#333',
      fontFamily: 'inherit',
      transition: 'all 0.3s ease',
    };
    
    // Apply styles to input
    Object.assign(input.style, styles);
    
    // Store data for retrieval
    wrapper.setAttribute('data-input-config', JSON.stringify(value));
    
    // Add focus/blur effects
    input.addEventListener('focus', function() {
      this.style.borderColor = value.focusBorderColor || '#0F3A61';
      this.style.outline = 'none';
      this.style.boxShadow = `0 0 0 2px ${value.focusBorderColor || '#0F3A61'}33`;
    });
    
    input.addEventListener('blur', function() {
      this.style.borderColor = value.borderColor || '#ccc';
      this.style.boxShadow = 'none';
    });
    
    // Prevent actual input in editor mode
    input.addEventListener('keydown', function(e) {
      e.preventDefault();
      e.stopPropagation();
    });
    
    input.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
    });
    
    // Add double-click to edit
    node.addEventListener('dblclick', function(e) {
      e.preventDefault();
      e.stopPropagation();
      
      // Get the latest value from the wrapper
      const wrapper = this.querySelector('.input-wrapper[data-input-config]');
      let currentValue = value;
      if (wrapper) {
        try {
          currentValue = JSON.parse(wrapper.getAttribute('data-input-config'));
        } catch (err) {
          // Use original value if parse fails
        }
      }
      
      // Trigger edit event
      const editEvent = new CustomEvent('input-edit', {
        detail: { node: this, value: currentValue },
        bubbles: true
      });
      this.dispatchEvent(editEvent);
    });
    
    wrapper.appendChild(input);
    node.appendChild(wrapper);
    
    return node;
  }
  
  static value(node) {
    const wrapper = node.querySelector('.input-wrapper[data-input-config]');
    if (wrapper) {
      try {
        return JSON.parse(wrapper.getAttribute('data-input-config'));
      } catch (e) {
        return {};
      }
    }
    return {};
  }
}

InputBlot.blotName = 'input';
InputBlot.tagName = 'div';
InputBlot.className = 'ql-custom-input';

export default InputBlot;